import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import {
  Check, Clock, AlertCircle, ArrowRight, ArrowLeft, ChevronDown, ChevronUp,
  Info, Zap, Shield, Star, Package, Users, Sparkles, Edit, Monitor, Share2, RefreshCw, Edit3,
  Upload, Image, MapPin, X
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// BackButton component (inline)
const BackButton = ({ to, label = "Back" }) => {
  const navigate = useNavigate();
  return (
    <button
      onClick={() => to ? navigate(to) : navigate(-1)}
      className="inline-flex items-center gap-2 text-zinc-400 hover:text-emerald-500 transition-colors mb-6 group"
      data-testid="back-button"
    >
      <ArrowLeft size={18} className="group-hover:-translate-x-1 transition-transform" />
      <span>{label}</span>
    </button>
  );
};

// This component receives useAuth as a prop from App.js
const RealEstateCheckoutPage = ({ useAuth }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [ordersPaused, setOrdersPaused] = useState(false);
  const [pauseMessage, setPauseMessage] = useState("");
  const [isTestMode, setIsTestMode] = useState(false);
  const [customPhotos, setCustomPhotos] = useState(5);
  const [rushDelivery, setRushDelivery] = useState(null);
  const [activeStep, setActiveStep] = useState(1);
  const [showPricingInfo, setShowPricingInfo] = useState(false);
  const [formData, setFormData] = useState({
    customer_name: user?.full_name || "",
    customer_email: user?.email || "",
    customer_phone: "",
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  });
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [couponCode, setCouponCode] = useState("");
  const [couponValid, setCouponValid] = useState(null);
  const [couponChecking, setCouponChecking] = useState(false);
  const [phoneError, setPhoneError] = useState("");

  // Agent Info for Title Card
  const [agentInfo, setAgentInfo] = useState({
    agent_name: "",
    brokerage_name: "",
    agent_phone: "",
    agent_email: "",
  });
  const [agentEmailError, setAgentEmailError] = useState("");
  const [agentPhoneError, setAgentPhoneError] = useState("");
  const [showOptionalBranding, setShowOptionalBranding] = useState(false);
  const [includeLogo, setIncludeLogo] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [includeLicense, setIncludeLicense] = useState(false);
  const [licenseNumber, setLicenseNumber] = useState("");
  const [includeLocation, setIncludeLocation] = useState(false);
  const [agentCity, setAgentCity] = useState("");
  const [agentState, setAgentState] = useState("");
  const [titleCardPlacement, setTitleCardPlacement] = useState("end");
  const [referralCode, setReferralCode] = useState("");
  const [referralValid, setReferralValid] = useState(null);
  const [referralChecking, setReferralChecking] = useState(false);

  // US States list
  const US_STATES = [
    "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA", "HI", "ID", "IL", "IN", "IA",
    "KS", "KY", "LA", "ME", "MD", "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
    "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC", "SD", "TN", "TX", "UT", "VT",
    "VA", "WA", "WV", "WI", "WY"
  ];

  // Pricing constants
  const RE_MIN_PHOTOS = 10;
  const RE_BASE_PRICE = 50.00;
  const RE_DISCOUNT_PRICE = 40.00;
  const RE_DISCOUNT_THRESHOLD = 50;
  const RE_RUSH_48HR = 50;
  const RE_RUSH_24HR = 100;

  // Fixed Real Estate Packages — NEW pricing
  const RE_PACKAGES = {
    "10photos": { id: "re_10photos", label: "10 Photos", photos: 10, price: 125, duration: "Property video", perPhoto: 12.5 },
    "25photos": { id: "re_25photos", label: "25 Photos", photos: 25, price: 200, duration: "Extended showcase", perPhoto: 8 },
    custom: { id: "re_custom", label: "Custom Package", photos: null, price: null, duration: "Varies", perPhoto: null },
  };
  
  // Read package from URL params
  const [selectedPackage, setSelectedPackage] = useState(() => {
    const pkg = searchParams.get("package");
    if (pkg && ["10photos", "25photos", "custom"].includes(pkg)) return pkg;
    // Map old params
    if (pkg === "small") return "10photos";
    if (pkg === "standard" || pkg === "luxury") return "25photos";
    return "25photos";
  });
  
  // Custom pricing state
  const [customPricing, setCustomPricing] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const settingsRes = await axios.get(`${API}/settings/public`);
        setOrdersPaused(settingsRes.data.orders_paused || false);
        setPauseMessage(settingsRes.data.pause_message || "We are currently not accepting new orders.");
        setIsTestMode(settingsRes.data.re_test_mode || false);
      } catch (err) {
        console.error("Failed to fetch settings:", err);
      }
    })();
  }, []);

  useEffect(() => {
    if (user) {
      setFormData(f => ({ ...f, customer_name: user.full_name, customer_email: user.email }));
    }
  }, [user]);
  
  // Fetch custom pricing when custom photos change
  useEffect(() => {
    if (selectedPackage === "custom" && customPhotos >= 5) {
      const fetchCustomPricing = async () => {
        try {
          const res = await axios.get(`${API}/packages/real-estate/custom-price?photos=${customPhotos}`);
          setCustomPricing(res.data);
        } catch (e) {
          console.error("Failed to fetch custom pricing:", e);
        }
      };
      fetchCustomPricing();
    }
  }, [selectedPackage, customPhotos]);

  // Track scroll to update progress indicator
  useEffect(() => {
    const handleScroll = () => {
      const sections = ['photos-section', 'delivery-section', 'info-section', 'checkout-section'];
      for (let i = sections.length - 1; i >= 0; i--) {
        const el = document.getElementById(sections[i]);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= 200) {
            setActiveStep(i + 1);
            break;
          }
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Calculate custom price — must match backend exactly, NEVER undercut tiers
  const calculateCustomPrice = (photos) => {
    if (photos < 5) return null;
    
    // Force tier pricing at exact tier counts
    if (photos === 10) return { total: 125, perPhoto: 12.5, isTier: true, tierName: "10 Photos" };
    if (photos === 25) return { total: 200, perPhoto: 8, isTier: true, tierName: "25 Photos" };
    
    // Custom bracket pricing: 5-9=$18, 10-24=$16, 25+=$14
    let perPhoto;
    if (photos < 10) perPhoto = 18;
    else if (photos < 25) perPhoto = 16;
    else perPhoto = 14;
    
    return { total: Math.round(photos * perPhoto), perPhoto, isTier: false, tierName: null };
  };

  const calculateTotal = () => {
    let total = 0;
    
    if (selectedPackage === "custom") {
      const pricing = customPricing || calculateCustomPrice(customPhotos);
      total = pricing?.total || 0;
    } else {
      const pkg = RE_PACKAGES[selectedPackage];
      total = pkg ? pkg.price : 0;
    }
    
    if (rushDelivery === "48hr") total += RE_RUSH_48HR;
    if (rushDelivery === "24hr") total += RE_RUSH_24HR;
    if (referralValid && referralValid.valid) total = Math.max(0, total - 25);
    if (couponValid && couponValid.valid) total = Math.max(0, total - couponValid.discount_amount);
    return total;
  };

  const getEstimatedDelivery = () => {
    const today = new Date();
    let days = 5; // Standard - 5 business days
    if (rushDelivery === "48hr") days = 2;
    if (rushDelivery === "24hr") days = 1;
    const delivery = new Date(today.setDate(today.getDate() + days));
    return delivery.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
  };

  const formatPhone = (value) => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length <= 3) return cleaned;
    if (cleaned.length <= 6) return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhone(e.target.value);
    setFormData({ ...formData, customer_phone: formatted });
    if (formatted.length < 14) {
      setPhoneError("Please enter a valid phone number");
    } else {
      setPhoneError("");
    }
  };

  // Agent phone formatting
  const handleAgentPhoneChange = (e) => {
    const formatted = formatPhone(e.target.value);
    setAgentInfo({ ...agentInfo, agent_phone: formatted });
    if (formatted.length < 14) {
      setAgentPhoneError("Please enter a valid phone number");
    } else {
      setAgentPhoneError("");
    }
  };

  // Agent email validation
  const handleAgentEmailChange = (e) => {
    const email = e.target.value;
    setAgentInfo({ ...agentInfo, agent_email: email });
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (email && !emailRegex.test(email)) {
      setAgentEmailError("Please enter a valid email address");
    } else {
      setAgentEmailError("");
    }
  };

  // Logo file handling
  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'];
      if (!validTypes.includes(file.type)) {
        setError("Please upload a PNG, JPG, or SVG file");
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setError("Logo file must be under 5MB");
        return;
      }
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setLogoPreview(reader.result);
      reader.readAsDataURL(file);
      setError("");
    }
  };

  const removeLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
  };

  const handleCheckout = async () => {
    if (ordersPaused) {
      setError("Orders are currently paused.");
      return;
    }
    if (!termsAccepted) {
      setError("Please accept the terms and conditions");
      return;
    }
    if (!formData.customer_name || !formData.customer_email || !formData.customer_phone) {
      const errs = {};
      if (!formData.customer_name) errs.customer_name = true;
      if (!formData.customer_email) errs.customer_email = true;
      if (!formData.customer_phone) errs.customer_phone = true;
      setFieldErrors(errs);
      setError("Please fill in all required fields");
      const el = document.querySelector('[data-field-error="true"]');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    if (formData.customer_phone.length < 14) {
      setFieldErrors({ customer_phone: true });
      setError("Please enter a valid phone number");
      return;
    }
    // Validate Agent Info (required fields)
    if (!agentInfo.agent_name || !agentInfo.brokerage_name || !agentInfo.agent_phone || !agentInfo.agent_email) {
      const errs = {};
      if (!agentInfo.agent_name) errs.agent_name = true;
      if (!agentInfo.brokerage_name) errs.brokerage_name = true;
      if (!agentInfo.agent_phone) errs.agent_phone = true;
      if (!agentInfo.agent_email) errs.agent_email = true;
      setFieldErrors(errs);
      setError("Please fill in all Agent Info fields for the title card");
      const el = document.querySelector('[data-field-error="true"]');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    if (agentInfo.agent_phone.length < 14) {
      setFieldErrors({ agent_phone: true });
      setError("Please enter a valid agent phone number");
      return;
    }
    setFieldErrors({});
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(agentInfo.agent_email)) {
      setError("Please enter a valid agent email address");
      return;
    }
    setError("");
    setLoading(true);

    let packageId, photoCount;
    
    if (selectedPackage === "custom") {
      packageId = "re_custom";
      photoCount = customPhotos;
    } else {
      const pkg = RE_PACKAGES[selectedPackage];
      packageId = pkg.id;
      photoCount = pkg.photos;
    }

    // Upload logo first if selected
    let logoUrl = null;
    if (includeLogo && logoFile) {
      try {
        const logoFormData = new FormData();
        logoFormData.append("file", logoFile);
        const logoRes = await axios.post(`${API}/upload/agent-logo`, logoFormData, {
          headers: { "Content-Type": "multipart/form-data" }
        });
        logoUrl = logoRes.data.url;
      } catch (e) {
        console.error("Logo upload failed:", e);
        // Continue without logo if upload fails
      }
    }
    
    try {
      const res = await axios.post(`${API}/checkout/create-session`, {
        package_id: packageId,
        vertical: "real_estate",
        custom_photos: photoCount,
        rush_delivery: rushDelivery === "48hr" || rushDelivery === "24hr",
        rush_type: rushDelivery,
        song_request: false,
        video_book: false,
        customer_name: formData.customer_name,
        customer_email: formData.customer_email,
        customer_phone: formData.customer_phone,
        timezone: formData.timezone,
        origin_url: window.location.origin,
        // Agent Info for Title Card
        agent_info: {
          agent_name: agentInfo.agent_name,
          brokerage_name: agentInfo.brokerage_name,
          agent_phone: agentInfo.agent_phone,
          agent_email: agentInfo.agent_email,
          license_number: includeLicense ? licenseNumber : null,
          city: includeLocation ? agentCity : null,
          state: includeLocation ? agentState : null,
          logo_url: logoUrl,
          title_card_placement: titleCardPlacement,
        },
        referral_code: referralValid ? referralCode.toUpperCase().trim() : null,
        coupon_code: couponValid && couponValid.valid ? couponCode.toUpperCase().trim() : null,
      });
      window.location.href = res.data.checkout_url;
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to create checkout session");
      setLoading(false);
    }
  };

  // Non-logged in user - show auth prompt
  if (!user) {
    return (
      <div className="min-h-screen pt-24 pb-12 px-4" data-testid="re-checkout-page">
        <div className="max-w-xl mx-auto">
          <BackButton to="/real-estate" label="Back to Real Estate" />
          <div className="card-dark p-8 text-center">
            <div className="w-20 h-20 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Users className="w-10 h-10 text-emerald-500" />
            </div>
            <h1 className="text-3xl font-bold font-serif mb-4">Create Your Free Account</h1>
            <p className="text-zinc-400 mb-6 text-lg">Sign up to start your property video order.</p>
            <div className="bg-zinc-800/50 rounded-lg p-6 mb-8 text-left">
              <h3 className="font-semibold text-emerald-500 mb-4 text-center">Why create an account?</h3>
              <ul className="space-y-3 text-zinc-300">
                <li className="flex items-start gap-3"><Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" /><span>Track your order status in real-time</span></li>
                <li className="flex items-start gap-3"><Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" /><span>Receive email notifications at every step</span></li>
                <li className="flex items-start gap-3"><Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" /><span>Access your video from any device</span></li>
                <li className="flex items-start gap-3"><Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" /><span>Request re-edits if needed</span></li>
              </ul>
            </div>
            <div className="space-y-4">
              <Link to="/signup?redirect=/real-estate/checkout" className="bg-emerald-500 hover:bg-emerald-400 text-black w-full flex items-center justify-center gap-2 text-lg py-4 rounded-lg font-semibold transition" data-testid="re-checkout-signup-btn">
                <Sparkles size={20} /> Create Your Free Account
              </Link>
              <div className="flex items-center gap-4">
                <div className="flex-1 border-t border-zinc-700"></div>
                <span className="text-zinc-500 text-sm">Already have an account?</span>
                <div className="flex-1 border-t border-zinc-700"></div>
              </div>
              <Link to="/login?redirect=/real-estate/checkout" className="block w-full bg-zinc-700 hover:bg-zinc-600 text-white py-3 px-6 rounded-lg font-semibold transition text-center" data-testid="re-checkout-login-btn">Log In</Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 pb-36 md:pb-12 px-4" data-testid="re-checkout-page">
      {/* Progress Indicator - Fixed at top */}
      <div className="fixed top-16 left-0 right-0 bg-black/95 backdrop-blur-sm border-b border-zinc-800 z-40 py-2 md:py-3">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex justify-between items-center">
            {[
              { num: 1, label: "Photos" },
              { num: 2, label: "Delivery" },
              { num: 3, label: "Info" },
              { num: 4, label: "Checkout" },
            ].map((step, i) => (
              <div key={step.num} className="flex items-center">
                <div className={`flex items-center gap-1 md:gap-2 ${activeStep >= step.num ? 'text-emerald-500' : 'text-zinc-500'}`}>
                  <div className={`w-6 h-6 md:w-7 md:h-7 rounded-full flex items-center justify-center text-xs md:text-sm font-bold ${activeStep >= step.num ? 'bg-emerald-500 text-black' : 'bg-zinc-800 text-zinc-500'}`}>
                    {activeStep > step.num ? <Check size={12} /> : step.num}
                  </div>
                  <span className="hidden sm:inline text-xs md:text-sm font-medium">{step.label}</span>
                </div>
                {i < 3 && <div className={`w-4 sm:w-8 md:w-16 h-0.5 mx-1 md:mx-2 ${activeStep > step.num ? 'bg-emerald-500' : 'bg-zinc-800'}`}></div>}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto pt-14 md:pt-16">
        {/* Test Mode Banner */}
        {isTestMode && (
          <div className="mb-4 bg-amber-500/20 border border-amber-500 rounded-lg p-4 flex items-center gap-3" data-testid="re-test-mode-banner">
            <div className="bg-amber-500 rounded-full p-2">
              <AlertCircle className="w-5 h-5 text-black" />
            </div>
            <div>
              <p className="font-semibold text-amber-400">Test Mode Active</p>
              <p className="text-sm text-amber-300/80">This checkout is in test mode. No real charges will be made. Use test card: 4242 4242 4242 4242</p>
            </div>
          </div>
        )}
        
        <BackButton to="/real-estate" label="Back to Real Estate" />
        
        {/* Hero Section */}
        <div className="text-center mb-6 md:mb-8">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold font-serif mb-2 md:mb-3">Create Your Property Video</h1>
          <p className="text-zinc-400 text-sm md:text-base mb-3 md:mb-4 px-2">Trusted by real estate professionals to turn listings into cinematic walkthroughs</p>
          
          {/* Trust Icons - 2x2 grid on mobile */}
          <div className="grid grid-cols-2 md:flex md:flex-wrap md:justify-center gap-2 md:gap-6 text-xs md:text-sm text-zinc-500 max-w-sm md:max-w-none mx-auto">
            <div className="flex items-center justify-center gap-1 md:gap-2"><Zap className="w-3 h-3 md:w-4 md:h-4 text-emerald-500" /><span>Fast Turnaround</span></div>
            <div className="flex items-center justify-center gap-1 md:gap-2"><Monitor className="w-3 h-3 md:w-4 md:h-4 text-emerald-500" /><span>MLS Ready</span></div>
            <div className="flex items-center justify-center gap-1 md:gap-2"><RefreshCw className="w-3 h-3 md:w-4 md:h-4 text-emerald-500" /><span>One Revision</span></div>
            <div className="flex items-center justify-center gap-1 md:gap-2"><Shield className="w-3 h-3 md:w-4 md:h-4 text-emerald-500" /><span>Secure Checkout</span></div>
          </div>
        </div>

        {ordersPaused && (
          <div className="bg-orange-500/20 border border-orange-500/50 rounded-lg p-4 mb-6 flex items-center gap-3">
            <AlertCircle className="text-orange-500 flex-shrink-0" />
            <p className="text-orange-200">{pauseMessage}</p>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left Column */}
          <div className="lg:col-span-2 space-y-6">
            
            {/* STEP 1: Package Selection */}
            <div id="photos-section" className="card-dark p-4 md:p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-7 h-7 md:w-8 md:h-8 bg-emerald-500 rounded-full flex items-center justify-center text-black font-bold text-xs md:text-sm">1</div>
                <h2 className="text-lg md:text-xl font-bold">Select Your Photo Package</h2>
              </div>
              
              {/* Fixed Tier Packages */}
              <div className="space-y-3 mb-6">
                {Object.entries(RE_PACKAGES).filter(([key]) => key !== "custom").map(([key, pkg]) => (
                  <button
                    key={key}
                    onClick={() => setSelectedPackage(key)}
                    className={`w-full p-4 rounded-lg border transition text-left ${
                      selectedPackage === key 
                        ? 'border-emerald-500 bg-emerald-500/10' 
                        : 'border-zinc-700 hover:border-zinc-600'
                    }`}
                    data-testid={`package-${key}`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-semibold text-base md:text-lg">{pkg.label}</div>
                        <div className="text-xs text-zinc-500">{pkg.photos} photos • {pkg.duration}</div>
                        <div className="text-xs text-emerald-400">Best Value</div>
                      </div>
                      <div className="text-right">
                        <div className={`text-xl md:text-2xl font-bold ${selectedPackage === key ? 'text-emerald-500' : 'text-white'}`}>
                          ${pkg.price.toLocaleString()}
                        </div>
                        {key === "25photos" && <span className="text-xs bg-emerald-500 text-black px-2 py-0.5 rounded-full">Popular</span>}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
              
              {/* Custom Package Section */}
              <div className="border-t border-zinc-700 pt-6">
                <button
                  onClick={() => setSelectedPackage("custom")}
                  className={`w-full p-4 rounded-lg border transition text-left ${
                    selectedPackage === "custom" 
                      ? 'border-emerald-500 bg-emerald-500/10' 
                      : 'border-zinc-700 hover:border-zinc-600'
                  }`}
                  data-testid="package-custom"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="text-2xl">✨</div>
                      <div>
                        <div className="font-semibold text-base md:text-lg">Custom Package</div>
                        <div className="text-xs text-zinc-500">Choose your own photo count (5+)</div>
                      </div>
                    </div>
                    <div className="text-right">
                      {selectedPackage === "custom" && customPricing && (
                        <div className="text-xl md:text-2xl font-bold text-emerald-500">
                          ${customPricing.total?.toLocaleString()}
                        </div>
                      )}
                      {selectedPackage !== "custom" && (
                        <div className="text-sm text-zinc-400">Select to customize</div>
                      )}
                    </div>
                  </div>
                </button>
                
                {/* Custom Package Details (expanded when selected) */}
                {selectedPackage === "custom" && (
                  <div className="mt-4 p-4 bg-zinc-800/50 rounded-lg border border-zinc-700">
                    <div className="mb-4">
                      <label className="block text-sm font-medium mb-2">Number of Photos</label>
                      <div className="flex items-center gap-4">
                        <input
                          type="range"
                          min="5"
                          max="150"
                          value={customPhotos}
                          onChange={(e) => setCustomPhotos(parseInt(e.target.value))}
                          className="flex-1 h-2 bg-zinc-700 rounded-lg appearance-none cursor-pointer accent-emerald-500"
                          data-testid="custom-photos-slider"
                        />
                        <input
                          type="number"
                          min="5"
                          max="300"
                          value={customPhotos}
                          onChange={(e) => setCustomPhotos(Math.max(5, Math.min(300, parseInt(e.target.value) || 5)))}
                          className="w-20 bg-zinc-800 border border-zinc-600 rounded px-3 py-2 text-center font-bold text-emerald-500"
                          data-testid="custom-photos-input"
                        />
                      </div>
                    </div>
                    
                    {/* Quick Select Buttons */}
                    <div className="flex flex-wrap gap-2 mb-4">
                      {[10, 25, 50, 75, 100].map(num => {
                        const isTier = [25, 50, 75].includes(num);
                        return (
                          <button
                            key={num}
                            onClick={() => setCustomPhotos(num)}
                            className={`px-3 py-1.5 rounded-full text-sm font-medium transition ${
                              customPhotos === num 
                                ? 'bg-emerald-500 text-black' 
                                : isTier
                                  ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/50 hover:bg-emerald-500/30'
                                  : 'bg-zinc-700 hover:bg-zinc-600'
                            }`}
                            data-testid={`quick-select-${num}`}
                          >
                            {num} photos
                            {isTier && <span className="ml-1 text-xs">★</span>}
                          </button>
                        );
                      })}
                    </div>
                    
                    {/* Pricing Display */}
                    {customPricing && (
                      <div className="bg-zinc-900 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-zinc-400">Total Price</span>
                          <span className="text-2xl font-bold text-emerald-500">${customPricing.total?.toLocaleString()}</span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-zinc-500">Price per photo</span>
                          <span className="text-emerald-400">${customPricing.per_photo || customPricing.perPhoto}/photo</span>
                        </div>
                        
                        {/* Tier Match Badge */}
                        {(customPricing.is_tier || customPricing.isTier) && (
                          <div className="mt-3 flex items-center gap-2 bg-emerald-500/20 border border-emerald-500/50 rounded-lg p-2">
                            <Check className="text-emerald-500" size={16} />
                            <span className="text-sm text-emerald-400">
                              Best Value! This matches our {customPricing.tier_name || customPricing.tierName} tier
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Pricing Tiers Info */}
                    <div className="mt-4 text-xs text-zinc-500">
                      <p className="mb-1">Flexible pricing. Tier packages offer the best savings.</p>
                      <div className="flex flex-wrap gap-x-4 gap-y-1">
                        <span>5–9 photos = $18/photo</span>
                        <span>10–24 photos = $16/photo</span>
                        <span>25+ photos = $14/photo</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* STEP 2: Delivery Speed */}
            <div id="delivery-section" className="card-dark p-4 md:p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-7 h-7 md:w-8 md:h-8 bg-emerald-500 rounded-full flex items-center justify-center text-black font-bold text-xs md:text-sm">2</div>
                <h2 className="text-lg md:text-xl font-bold">Delivery Speed</h2>
              </div>
              
              <div className="space-y-2 md:space-y-3">
                {/* Standard */}
                <label className={`flex items-center justify-between p-3 md:p-4 rounded-lg border cursor-pointer transition ${rushDelivery === null ? 'border-emerald-500 bg-emerald-500/10' : 'border-zinc-700 hover:border-zinc-600'}`}>
                  <div className="flex items-center gap-2 md:gap-3">
                    <input type="radio" name="delivery" checked={rushDelivery === null} onChange={() => setRushDelivery(null)} className="accent-emerald-500 w-4 h-4" />
                    <div>
                      <div className="font-semibold text-sm md:text-base">Standard Delivery</div>
                      <div className="text-xs md:text-sm text-zinc-500">Est. {getEstimatedDelivery()}</div>
                    </div>
                  </div>
                  <div className="text-zinc-400 text-sm md:text-base">Included</div>
                </label>

                {/* 48hr Rush */}
                <label className={`flex items-center justify-between p-3 md:p-4 rounded-lg border cursor-pointer transition relative ${rushDelivery === "48hr" ? 'border-emerald-500 bg-emerald-500/10' : 'border-zinc-700 hover:border-zinc-600'}`}>
                  <div className="absolute -top-2 right-2 md:right-4 bg-amber-500 text-black text-[10px] md:text-xs font-bold px-1.5 md:px-2 py-0.5 rounded-full">POPULAR</div>
                  <div className="flex items-center gap-2 md:gap-3">
                    <input type="radio" name="delivery" checked={rushDelivery === "48hr"} onChange={() => setRushDelivery("48hr")} className="accent-emerald-500 w-4 h-4" />
                    <div>
                      <div className="font-semibold flex items-center gap-1 md:gap-2 text-sm md:text-base">48-Hour <Zap className="w-3 h-3 md:w-4 md:h-4 text-amber-500" /></div>
                      <div className="text-xs md:text-sm text-zinc-500">Priority editing</div>
                    </div>
                  </div>
                  <div className="font-bold text-emerald-500 text-sm md:text-base">+${RE_RUSH_48HR}</div>
                </label>

                {/* 24hr Rush */}
                <label className={`flex items-center justify-between p-3 md:p-4 rounded-lg border cursor-pointer transition ${rushDelivery === "24hr" ? 'border-emerald-500 bg-emerald-500/10' : 'border-zinc-700 hover:border-zinc-600'}`}>
                  <div className="flex items-center gap-2 md:gap-3">
                    <input type="radio" name="delivery" checked={rushDelivery === "24hr"} onChange={() => setRushDelivery("24hr")} className="accent-emerald-500 w-4 h-4" />
                    <div>
                      <div className="font-semibold flex items-center gap-1 md:gap-2 text-sm md:text-base">24-Hour <Zap className="w-3 h-3 md:w-4 md:h-4 text-red-500" /></div>
                      <div className="text-xs md:text-sm text-zinc-500">Next-day delivery</div>
                    </div>
                  </div>
                  <div className="font-bold text-emerald-500 text-sm md:text-base">+${RE_RUSH_24HR}</div>
                </label>
              </div>
            </div>

            {/* STEP 3: Your Information */}
            <div id="info-section" className="card-dark p-4 md:p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-7 h-7 md:w-8 md:h-8 bg-emerald-500 rounded-full flex items-center justify-center text-black font-bold text-xs md:text-sm">3</div>
                <h2 className="text-lg md:text-xl font-bold">Your Information</h2>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Full Name *</label>
                  <input
                    type="text" value={formData.customer_name}
                    onChange={(e) => { setFormData({...formData, customer_name: e.target.value}); if (fieldErrors.customer_name) setFieldErrors(p => ({...p, customer_name: false})); }}
                    className={`w-full bg-zinc-800 border rounded-lg px-4 py-3 focus:outline-none focus:border-emerald-500 ${fieldErrors.customer_name ? 'border-red-500 ring-1 ring-red-500/50' : 'border-zinc-700'}`}
                    data-field-error={fieldErrors.customer_name ? "true" : undefined}
                    required data-testid="re-customer-name"
                  />
                  {fieldErrors.customer_name && <p className="text-red-400 text-xs mt-1">Name is required</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Email *</label>
                  <input
                    type="email" value={formData.customer_email}
                    onChange={(e) => { setFormData({...formData, customer_email: e.target.value}); if (fieldErrors.customer_email) setFieldErrors(p => ({...p, customer_email: false})); }}
                    className={`w-full bg-zinc-800 border rounded-lg px-4 py-3 focus:outline-none focus:border-emerald-500 ${fieldErrors.customer_email ? 'border-red-500 ring-1 ring-red-500/50' : 'border-zinc-700'}`}
                    data-field-error={fieldErrors.customer_email ? "true" : undefined}
                    required data-testid="re-customer-email"
                  />
                  {fieldErrors.customer_email && <p className="text-red-400 text-xs mt-1">Email is required</p>}
                  <p className="text-xs text-zinc-500 mt-1">We&apos;ll only use your email for order updates</p>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Phone *</label>
                  <input
                    type="tel" value={formData.customer_phone}
                    onChange={handlePhoneChange}
                    className={`w-full bg-zinc-800 border rounded-lg px-4 py-3 focus:outline-none ${fieldErrors.customer_phone || phoneError ? 'border-red-500 ring-1 ring-red-500/50' : 'border-zinc-700 focus:border-emerald-500'}`}
                    data-field-error={fieldErrors.customer_phone ? "true" : undefined}
                    placeholder="(555) 123-4567" required data-testid="re-customer-phone"
                  />
                  {(fieldErrors.customer_phone || phoneError) && <p className="text-xs text-red-400 mt-1">{phoneError || "Phone is required"}</p>}
                </div>
              </div>
            </div>

            {/* STEP 4: Agent Info for Title Card */}
            <div id="agent-section" className="card-dark p-4 md:p-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-7 h-7 md:w-8 md:h-8 bg-emerald-500 rounded-full flex items-center justify-center text-black font-bold text-xs md:text-sm">4</div>
                <h2 className="text-lg md:text-xl font-bold">Agent Info for Title Card</h2>
              </div>
              <p className="text-xs text-zinc-500 mb-4 ml-10 md:ml-11">Viewers will see this in the video. Less is more.</p>
              
              {/* Required Fields */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Agent Full Name *</label>
                  <input
                    type="text"
                    value={agentInfo.agent_name}
                    onChange={(e) => { setAgentInfo({...agentInfo, agent_name: e.target.value}); if (fieldErrors.agent_name) setFieldErrors(p => ({...p, agent_name: false})); }}
                    className={`w-full bg-zinc-800 border rounded-lg px-4 py-3 focus:outline-none focus:border-emerald-500 ${fieldErrors.agent_name ? 'border-red-500 ring-1 ring-red-500/50' : 'border-zinc-700'}`}
                    data-field-error={fieldErrors.agent_name ? "true" : undefined}
                    placeholder="Jane Smith"
                    required
                    data-testid="agent-name"
                  />
                  {fieldErrors.agent_name && <p className="text-red-400 text-xs mt-1">Agent name is required</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Brokerage Name *</label>
                  <input
                    type="text"
                    value={agentInfo.brokerage_name}
                    onChange={(e) => { setAgentInfo({...agentInfo, brokerage_name: e.target.value}); if (fieldErrors.brokerage_name) setFieldErrors(p => ({...p, brokerage_name: false})); }}
                    className={`w-full bg-zinc-800 border rounded-lg px-4 py-3 focus:outline-none focus:border-emerald-500 ${fieldErrors.brokerage_name ? 'border-red-500 ring-1 ring-red-500/50' : 'border-zinc-700'}`}
                    data-field-error={fieldErrors.brokerage_name ? "true" : undefined}
                    placeholder="Premier Realty Group"
                    required
                    data-testid="brokerage-name"
                  />
                  {fieldErrors.brokerage_name && <p className="text-red-400 text-xs mt-1">Brokerage name is required</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Phone Number *</label>
                  <input
                    type="tel"
                    value={agentInfo.agent_phone}
                    onChange={handleAgentPhoneChange}
                    className={`w-full bg-zinc-800 border rounded-lg px-4 py-3 focus:outline-none ${fieldErrors.agent_phone || agentPhoneError ? 'border-red-500 ring-1 ring-red-500/50' : 'border-zinc-700 focus:border-emerald-500'}`}
                    data-field-error={fieldErrors.agent_phone ? "true" : undefined}
                    placeholder="(555) 123-4567"
                    required
                    data-testid="agent-phone"
                  />
                  {(fieldErrors.agent_phone || agentPhoneError) && <p className="text-xs text-red-400 mt-1">{agentPhoneError || "Phone is required"}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Email Address *</label>
                  <input
                    type="email"
                    value={agentInfo.agent_email}
                    onChange={handleAgentEmailChange}
                    className={`w-full bg-zinc-800 border rounded-lg px-4 py-3 focus:outline-none ${fieldErrors.agent_email || agentEmailError ? 'border-red-500 ring-1 ring-red-500/50' : 'border-zinc-700 focus:border-emerald-500'}`}
                    data-field-error={fieldErrors.agent_email ? "true" : undefined}
                    placeholder="jane@premierrealty.com"
                    required
                    data-testid="agent-email"
                  />
                  {(fieldErrors.agent_email || agentEmailError) && <p className="text-xs text-red-400 mt-1">{agentEmailError || "Email is required"}</p>}
                </div>
              </div>

              {/* Optional Branding (Collapsible) — visually prominent */}
              <div className="mt-6 border border-yellow-500/30 rounded-xl p-1" style={{ boxShadow: '0 0 12px rgba(234,179,8,0.08)' }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowOptionalBranding(!showOptionalBranding);
                    if (!showOptionalBranding) {
                      setTimeout(() => {
                        document.getElementById('branding-section')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      }, 100);
                    }
                  }}
                  className="flex items-center justify-between w-full text-left px-4 py-3 rounded-lg hover:bg-zinc-800/50 transition"
                  data-testid="optional-branding-toggle"
                >
                  <div className="flex items-center gap-2">
                    <Sparkles size={16} className="text-yellow-500" />
                    <span className="text-sm font-semibold text-white">Optional Branding</span>
                    <span className="text-xs text-zinc-500 ml-1">Add your logo and brand colors to your title card</span>
                  </div>
                  {showOptionalBranding ? (
                    <ChevronUp className="w-5 h-5 text-yellow-500" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-yellow-500" />
                  )}
                </button>
                
                {showOptionalBranding && (
                  <div id="branding-section" className="mt-4 space-y-4 px-4 pb-4">
                    {/* Brokerage Logo */}
                    <div className="bg-zinc-800/50 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <label className="text-sm font-medium">Upload Brokerage Logo</label>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={includeLogo}
                            onChange={(e) => {
                              setIncludeLogo(e.target.checked);
                              if (!e.target.checked) removeLogo();
                            }}
                            className="sr-only peer"
                          />
                          <div className="w-9 h-5 bg-zinc-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-emerald-500"></div>
                        </label>
                      </div>
                      {includeLogo && (
                        <div>
                          <p className="text-xs text-zinc-500 mb-2">PNG, JPG, or SVG (transparent PNG preferred)</p>
                          {logoPreview ? (
                            <div className="relative inline-block">
                              <img src={logoPreview} alt="Logo preview" className="h-16 max-w-[200px] object-contain bg-zinc-900 rounded p-2" />
                              <button
                                type="button"
                                onClick={removeLogo}
                                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          ) : (
                            <label className="flex items-center gap-2 cursor-pointer bg-zinc-700 hover:bg-zinc-600 px-4 py-2 rounded-lg transition w-fit">
                              <Upload className="w-4 h-4" />
                              <span className="text-sm">Choose File</span>
                              <input
                                type="file"
                                accept=".png,.jpg,.jpeg,.svg"
                                onChange={handleLogoChange}
                                className="hidden"
                                data-testid="logo-upload"
                              />
                            </label>
                          )}
                        </div>
                      )}
                    </div>

                    {/* License Number */}
                    <div className="bg-zinc-800/50 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <label className="text-sm font-medium">Include License Number</label>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={includeLicense}
                            onChange={(e) => setIncludeLicense(e.target.checked)}
                            className="sr-only peer"
                          />
                          <div className="w-9 h-5 bg-zinc-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-emerald-500"></div>
                        </label>
                      </div>
                      {includeLicense && (
                        <input
                          type="text"
                          value={licenseNumber}
                          onChange={(e) => setLicenseNumber(e.target.value)}
                          className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-sm focus:outline-none focus:border-emerald-500"
                          placeholder="DRE #01234567"
                          data-testid="license-number"
                        />
                      )}
                    </div>

                    {/* City & State */}
                    <div className="bg-zinc-800/50 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <label className="text-sm font-medium">Include City & State</label>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={includeLocation}
                            onChange={(e) => setIncludeLocation(e.target.checked)}
                            className="sr-only peer"
                          />
                          <div className="w-9 h-5 bg-zinc-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-emerald-500"></div>
                        </label>
                      </div>
                      {includeLocation && (
                        <div className="flex gap-3">
                          <input
                            type="text"
                            value={agentCity}
                            onChange={(e) => setAgentCity(e.target.value)}
                            className="flex-1 bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-sm focus:outline-none focus:border-emerald-500"
                            placeholder="City"
                            data-testid="agent-city"
                          />
                          <select
                            value={agentState}
                            onChange={(e) => setAgentState(e.target.value)}
                            className="w-24 bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-emerald-500"
                            data-testid="agent-state"
                          >
                            <option value="">State</option>
                            {US_STATES.map(st => (
                              <option key={st} value={st}>{st}</option>
                            ))}
                          </select>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Title Card Placement */}
              <div className="mt-6 border-t border-zinc-700 pt-4">
                <label className="block text-sm font-medium mb-3">Title Card Placement</label>
                <div className="flex flex-wrap gap-3">
                  {[
                    { value: "beginning", label: "Beginning" },
                    { value: "end", label: "End" },
                    { value: "both", label: "Both" },
                  ].map(opt => (
                    <label
                      key={opt.value}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg border cursor-pointer transition ${
                        titleCardPlacement === opt.value
                          ? 'border-emerald-500 bg-emerald-500/10 text-emerald-400'
                          : 'border-zinc-700 hover:border-zinc-600'
                      }`}
                    >
                      <input
                        type="radio"
                        name="titleCardPlacement"
                        value={opt.value}
                        checked={titleCardPlacement === opt.value}
                        onChange={(e) => setTitleCardPlacement(e.target.value)}
                        className="sr-only"
                      />
                      <span className="text-sm">{opt.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {/* What's Included - Expanded */}
            <div className="card-dark p-4 md:p-6">
              <h3 className="text-base md:text-lg font-bold mb-3 md:mb-4">What&apos;s Included</h3>
              <div className="grid grid-cols-2 gap-3 md:gap-4">
                <div className="flex items-start gap-2 md:gap-3">
                  <div className="w-7 h-7 md:w-8 md:h-8 bg-emerald-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Edit3 className="w-3 h-3 md:w-4 md:h-4 text-emerald-500" />
                  </div>
                  <div>
                    <div className="font-medium text-sm md:text-base">Pro Editing</div>
                    <div className="text-xs md:text-sm text-zinc-500">Hand-edited</div>
                  </div>
                </div>
                <div className="flex items-start gap-2 md:gap-3">
                  <div className="w-7 h-7 md:w-8 md:h-8 bg-emerald-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Monitor className="w-3 h-3 md:w-4 md:h-4 text-emerald-500" />
                  </div>
                  <div>
                    <div className="font-medium text-sm md:text-base">HD Video</div>
                    <div className="text-xs md:text-sm text-zinc-500">MLS ready</div>
                  </div>
                </div>
                <div className="flex items-start gap-2 md:gap-3">
                  <div className="w-7 h-7 md:w-8 md:h-8 bg-emerald-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <RefreshCw className="w-3 h-3 md:w-4 md:h-4 text-emerald-500" />
                  </div>
                  <div>
                    <div className="font-medium text-sm md:text-base">Free Revision</div>
                    <div className="text-xs md:text-sm text-zinc-500">Tweaks included</div>
                  </div>
                </div>
                <div className="flex items-start gap-2 md:gap-3">
                  <div className="w-7 h-7 md:w-8 md:h-8 bg-emerald-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Share2 className="w-3 h-3 md:w-4 md:h-4 text-emerald-500" />
                  </div>
                  <div>
                    <div className="font-medium text-sm md:text-base">Social Ready</div>
                    <div className="text-xs md:text-sm text-zinc-500">Clean pacing</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Sticky Order Summary */}
          <div className="lg:col-span-1">
            <div id="checkout-section" className="card-dark p-6 lg:sticky lg:top-32">
              <h2 className="text-xl font-bold mb-4">Order Summary</h2>
              
              <div className="space-y-3 mb-6 text-sm">
                <div className="flex justify-between">
                  <span className="text-zinc-400">
                    {selectedPackage === "custom" 
                      ? (customPricing?.is_tier || customPricing?.isTier 
                          ? customPricing.tier_name || customPricing.tierName 
                          : `Custom (${customPhotos} photos)`)
                      : RE_PACKAGES[selectedPackage]?.label}
                  </span>
                  <span>
                    ${selectedPackage === "custom" 
                      ? (customPricing?.total || 0).toLocaleString()
                      : RE_PACKAGES[selectedPackage]?.price.toLocaleString()}
                  </span>
                </div>
                <div className="text-xs text-zinc-500">
                  {selectedPackage === "custom" 
                    ? `${customPhotos} photos • ${customPricing?.per_photo || customPricing?.perPhoto || 0}/photo`
                    : `${RE_PACKAGES[selectedPackage]?.photos} photos • ${RE_PACKAGES[selectedPackage]?.duration}`}
                </div>
                
                {rushDelivery === "48hr" && (
                  <div className="flex justify-between text-amber-500">
                    <span>48-Hour Rush</span>
                    <span>+${RE_RUSH_48HR}</span>
                  </div>
                )}
                
                {rushDelivery === "24hr" && (
                  <div className="flex justify-between text-red-400">
                    <span>24-Hour Rush</span>
                    <span>+${RE_RUSH_24HR}</span>
                  </div>
                )}
                
                {couponValid && couponValid.valid && (
                  <div className="flex justify-between text-sm text-emerald-400">
                    <span>Coupon ({couponCode})</span>
                    <span>-${couponValid.discount_amount}</span>
                  </div>
                )}
                <div className="border-t border-zinc-700 pt-3 flex justify-between text-lg font-bold">
                  <span>Total</span>
                  <span className="text-emerald-500">${calculateTotal().toLocaleString()}</span>
                </div>
              </div>

              {/* Trust Badges */}
              <div className="bg-zinc-800/50 rounded-lg p-4 mb-6 space-y-2 text-xs text-zinc-400">
                <div className="flex items-center gap-2"><Shield className="w-4 h-4 text-emerald-500" /> Secure Stripe Payments</div>
                <div className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-500" /> No Hidden Fees</div>
                <div className="flex items-center gap-2"><RefreshCw className="w-4 h-4 text-emerald-500" /> One Free Revision Included</div>
              </div>

              {error && (
                <div className="bg-red-500/20 border border-red-500/50 text-red-400 p-3 rounded-lg mb-4 text-sm">{error}</div>
              )}

              {/* Referral Code */}
              <div className="mb-4">
                <label className="block text-xs text-zinc-400 mb-1">Referral Code (optional)</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={referralCode}
                    onChange={(e) => { setReferralCode(e.target.value.toUpperCase()); setReferralValid(null); }}
                    placeholder="RE-XXXXXX"
                    className="flex-1 bg-zinc-800 border border-zinc-600 rounded px-3 py-2 text-sm text-white placeholder-zinc-500"
                    data-testid="re-referral-input"
                  />
                  <button
                    onClick={async () => {
                      if (!referralCode.trim()) return;
                      setReferralChecking(true);
                      try {
                        const r = await axios.get(`${API}/re-referral/validate/${referralCode.trim()}`);
                        setReferralValid(r.data);
                      } catch (e) {
                        setReferralValid({ valid: false, message: e.response?.data?.detail || "Invalid code" });
                      }
                      setReferralChecking(false);
                    }}
                    disabled={referralChecking || !referralCode.trim()}
                    className="px-3 py-2 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 rounded text-sm font-medium disabled:opacity-40"
                    data-testid="re-referral-apply-btn"
                  >
                    {referralChecking ? "..." : "Apply"}
                  </button>
                </div>
                {referralValid && referralValid.valid && (
                  <p className="text-emerald-400 text-xs mt-1">$25 discount applied!</p>
                )}
                {referralValid && !referralValid.valid && (
                  <p className="text-red-400 text-xs mt-1">{referralValid.message}</p>
                )}
              </div>

              {/* Coupon Code */}
              <div className="mb-4">
                <label className="block text-xs text-zinc-400 mb-1">Coupon Code (optional)</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={couponCode}
                    onChange={(e) => { setCouponCode(e.target.value.toUpperCase()); setCouponValid(null); }}
                    placeholder="Enter code"
                    className="flex-1 bg-zinc-800 border border-zinc-600 rounded px-3 py-2 text-sm text-white placeholder-zinc-500"
                    data-testid="re-coupon-input"
                  />
                  <button
                    onClick={async () => {
                      if (!couponCode.trim()) return;
                      setCouponChecking(true);
                      try {
                        const r = await axios.get(`${API}/coupons/validate/${couponCode.trim()}?vertical=real_estate`);
                        setCouponValid(r.data);
                      } catch (e) {
                        setCouponValid({ valid: false, message: e.response?.data?.detail || "Invalid code" });
                      }
                      setCouponChecking(false);
                    }}
                    disabled={couponChecking || !couponCode.trim()}
                    className="px-3 py-2 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 rounded text-sm font-medium disabled:opacity-40"
                    data-testid="re-coupon-apply-btn"
                  >
                    {couponChecking ? "..." : "Apply"}
                  </button>
                </div>
                {couponValid && couponValid.valid && (
                  <p className="text-emerald-400 text-xs mt-1">✓ ${couponValid.discount_amount} discount applied!</p>
                )}
                {couponValid && !couponValid.valid && (
                  <p className="text-red-400 text-xs mt-1">{couponValid.message}</p>
                )}
              </div>

              {/* Terms */}
              <div className="flex items-start gap-3 mb-4">
                <input type="checkbox" id="re-terms" checked={termsAccepted} onChange={(e) => setTermsAccepted(e.target.checked)}
                  className="mt-1 accent-emerald-500" data-testid="re-terms-checkbox" />
                <label htmlFor="re-terms" className="text-xs text-zinc-400">
                  I agree to the <Link to="/terms?vertical=real_estate" className="text-emerald-500 hover:underline">Terms of Service</Link> and <Link to="/privacy?vertical=real_estate" className="text-emerald-500 hover:underline">Privacy Policy</Link>
                </label>
              </div>

              {/* Guarantee */}
              <p className="text-center text-xs text-zinc-500 mb-3">If you&apos;re not happy, we&apos;ll revise it</p>

              <button
                onClick={handleCheckout}
                disabled={loading || ordersPaused}
                className="w-full bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed text-black py-4 rounded-lg font-semibold text-lg transition flex items-center justify-center gap-2"
                data-testid="re-checkout-submit"
              >
                {loading ? (
                  <><RefreshCw className="w-5 h-5 animate-spin" /> Processing...</>
                ) : (
                  <>Create Your Video <ArrowRight className="w-5 h-5" /></>
                )}
              </button>

              <p className="text-xs text-zinc-500 text-center mt-3">You&apos;ll upload photos on the next step</p>

              {/* Testimonial */}
              <div className="mt-6 pt-6 border-t border-zinc-800">
                <p className="text-sm text-zinc-400 italic text-center">&quot;Our listing video helped increase showing requests within days.&quot;</p>
                <p className="text-xs text-zinc-500 text-center mt-1">— Real Estate Professional</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Sticky CTA */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-black/95 backdrop-blur-sm border-t border-zinc-800 p-4 z-50">
        <div className="flex items-center justify-between mb-2">
          <span className="text-zinc-400">Total</span>
          <span className="text-xl font-bold text-emerald-500">${calculateTotal().toLocaleString()}</span>
        </div>
        <button
          onClick={handleCheckout}
          disabled={loading || ordersPaused || !termsAccepted}
          className="w-full bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-black py-3 rounded-lg font-semibold transition"
        >
          {loading ? "Processing..." : "Create Your Video →"}
        </button>
      </div>
    </div>
  );
};


export default RealEstateCheckoutPage;